import { Popconfirm } from 'antd';
import { SlRefresh } from 'react-icons/sl';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageInstance } from 'antd/es/message/interface';
import { AppButton, Flex } from '../../common';
import { useSelfContext } from '@/context';
import { ReserveHttp } from '@/services';
import { useAppStore, useCartStore } from '@/store';
import { ReactComponent as IcPlusBasket } from '@/assets/images/ic-basket.svg';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { useProgramContext } from '@/context/ProgramContext/ProgramContext';
import {
  canAddProgramToCartWeek,
  resolveCartWeekStartMs,
} from '@/utils/cartWeekValidation';
import {
  getMaxAddableCount,
  isDecrementDisabled,
  isIncrementDisabled,
  resolveReserveIncrement,
  shouldShowBulkReserveInput,
} from './reserveCountLimits';
import {
  getCombinedDisplayCount,
  getProgramCartCount,
  getServerReserveCount,
} from './reserveDisplayCounts';
import BulkReserveControls from './BulkReserveControls';

export interface ReserveActionButtonsProps {
  isNewReserveMode?: boolean;
  panelWeekStartDate?: string;
  meal: SelfMealEntity;
  reserve: UserWeekReserveEntity;
  selectedCount: number;
  messageAPI: MessageInstance;
  setSelectedCount: React.Dispatch<React.SetStateAction<number>>;
  onChangeSelf: () => void;
  locked: boolean;
  setLocked: (value: boolean) => any;
}

export default function ReserveActionButtons({
  isNewReserveMode = false,
  panelWeekStartDate,
  reserve,
  meal,
  onChangeSelf,
  selectedCount,
  setSelectedCount,
  messageAPI,
  locked,
  setLocked
}: ReserveActionButtonsProps) {

  const { fetchCredit, apiVersion } = useAppStore();
  const { currentSelf } = useSelfContext();
  const addToCart = useCartStore(state => state.addToCart);
  const removeFromCart = useCartStore(state => state.removeFromCart);
  const cartPrograms = useCartStore(state => state.programs);
  const cartWeekStartDateTime = useCartStore(state => state.weekStartDateTime);
  const [t] = useTranslation(['common', 'reserve']);
  const { fetchCurrentSelfPrograms } = useProgramContext();
  const [isIncrementing, setIsIncrementing] = useState(false);
  const [isDecrementing, setIsDecrementing] = useState(false);
  const [saleable, setSaleable] = useState(reserve?.saleableFreeFood);
  const [buyableFreeFood, setBuyableFreeFood] = useState(meal?.buyableFreeFood);
  const [forSale, setForSale] = useState(reserve?.forSale);
  const reservable = !meal.reserveRuleViolated;
  const cancelable = !meal.cancelRuleViolated;
  const freeRestaurant = Boolean(currentSelf?.freeRestaurant);
  const showBulkFlow = shouldShowBulkReserveInput(reserve) && !freeRestaurant;
  const countRef = useRef<number>(selectedCount);

  useEffect(() => {
    const programCartCount = getProgramCartCount(cartPrograms, reserve.programId);
    countRef.current = freeRestaurant ? programCartCount : selectedCount;
  }, [freeRestaurant, cartPrograms, reserve.programId, selectedCount]);

  const mealCartTotal = useMemo(() => {
    if (!freeRestaurant) {
      return 0;
    }
    return getProgramCartCount(cartPrograms, reserve.programId);
  }, [freeRestaurant, cartPrograms, reserve.programId]);

  const limitArgs = useMemo(() => ({
    selectedCount,
    reserve,
    meal,
    mealCartTotal,
    freeRestaurant,
  }), [selectedCount, reserve, meal, mealCartTotal, freeRestaurant]);

  const maxAddable = useMemo(() => getMaxAddableCount(limitArgs), [limitArgs]);
  const isIncDisabled = isIncrementDisabled(limitArgs);
  const isDecDisabled = isDecrementDisabled(selectedCount, reserve);

  const handleReserve = useCallback(async (requestedCount?: number) => {
    const reserveCount = resolveReserveIncrement({
      selectedCount,
      reserve,
      requestedCount,
      maxAddable,
    });
    if (reserveCount <= 0) {
      return;
    }

    if (freeRestaurant) {
      if (mealCartTotal + reserveCount > meal.validTotalCount || selectedCount + reserveCount > reserve.validMaxCount) {
        return;
      }
      if (
        !canAddProgramToCartWeek(
          cartWeekStartDateTime,
          cartPrograms,
          panelWeekStartDate,
          meal.date,
        )
      ) {
        messageAPI.error(t('common:errors.cart-multi-week-not-allowed'));
        return;
      }
    }

    setIsIncrementing(true);
    setLocked(true);
    try {
      if (freeRestaurant) {
        const cartBefore = getProgramCartCount(cartPrograms, reserve.programId);
        addToCart({
          id: reserve.programId,
          food: {
            ...reserve,
            selfId: reserve.selfId ?? meal.selfId,
            programDate: reserve.programDate ?? meal.date,
            programDateTime: reserve.programDateTime ?? meal.dateTime,
            foodId: reserve.foodId
          },
          checkoutSelfDefId: currentSelf.id,
          weekStartDateTime: resolveCartWeekStartMs(panelWeekStartDate, meal.date),
        }, reserveCount);
        setSelectedCount(getServerReserveCount(reserve) + cartBefore + reserveCount);
        messageAPI.success(t('reserve:messages.success-add-to-cart'));
        return;
      }

      countRef.current += reserveCount;
      setSelectedCount(_c => {
        reserve.selectedCount = countRef.current;
        return countRef.current;
      });
      if (isNewReserveMode) {
        const response = await ReserveHttp.reserveNewAPI(reserve.programId, {
          count: reserveCount,
        });

        if (!reserve.id) {
          reserve.id = response.payload.ids[0];
        }
        else {
          if (!reserve.idAndConsumption) {
            reserve.idAndConsumption = {};
          }
          for (const key of (response.payload.ids ?? [])) {
            reserve.idAndConsumption[key] = false;
          }
        }
      }
      else {
        const newReserve = await ReserveHttp.reserve(reserve.programId, {
          foodTypeId: reserve.foodTypeId,
          mealTypeId: reserve.mealTypeId,
          selectedCount: countRef.current,
          freeFoodSelected: false,
        });
        reserve.id = newReserve?.payload?.id;
      }

      messageAPI.success(t('reserve:messages.success-reserve'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
      if (freeRestaurant) {
        setSelectedCount(getCombinedDisplayCount(reserve, cartPrograms));
      }
      else {
        countRef.current = countRef.current - reserveCount;
        reserve.selectedCount = countRef.current;
        setSelectedCount(countRef.current);
      }
    }
    finally {
      setIsIncrementing(false);
      setLocked(false);
    }
  }, [
    selectedCount,
    reserve,
    meal,
    maxAddable,
    freeRestaurant,
    mealCartTotal,
    cartPrograms,
    cartWeekStartDateTime,
    panelWeekStartDate,
    addToCart,
    currentSelf,
    fetchCredit,
    messageAPI,
    t,
    isNewReserveMode,
    setLocked,
    setSelectedCount,
  ]);

  const handleCancelReserve = useCallback(async () => {
    setIsDecrementing(true);
    setLocked(true);
    const serverCount = getServerReserveCount(reserve);
    const cartCount = getProgramCartCount(cartPrograms, reserve.programId);

    try {
      if (freeRestaurant) {
        if (cartCount > 0) {
          removeFromCart(reserve.programId);
          setSelectedCount(serverCount + Math.max(0, cartCount - 1));
          messageAPI.success(t('reserve:messages.success-remove-from-cart'));
          return;
        }
        if (serverCount <= 0 || !reserve.id) {
          return;
        }
      }
      else {
        setSelectedCount(c => {
          if (c <= 0) {
            countRef.current = 0;
            return 0;
          }
          countRef.current = c - 1;
          reserve.selectedCount = countRef.current;
          return countRef.current;
        });
      }

      const cancelSelectedCount = freeRestaurant ? serverCount - 1 : countRef.current;

      if (apiVersion !== null) {
        await ReserveHttp.cancelByReserveId(reserve.id);
      } else {
        await ReserveHttp.cancel(reserve.programId, {
          foodTypeId: reserve.foodTypeId,
          mealTypeId: reserve.mealTypeId,
          selectedCount: cancelSelectedCount,
          freeFoodSelected: false,
        });
      }

      const firstkey = Object.keys(reserve.idAndConsumption ?? {})[0];

      if (firstkey) {
        reserve.id = Number(firstkey);
        delete reserve.idAndConsumption[firstkey];
      }
      else {
        reserve.id = undefined;
      }

      if (freeRestaurant) {
        reserve.selectedCount = Math.max(0, serverCount - 1);
        if (reserve.selectedCount === 0) {
          reserve.id = undefined;
        }
        setSelectedCount(
          getProgramCartCount(cartPrograms, reserve.programId) + (reserve.selectedCount ?? 0),
        );
      }

      messageAPI.success(t('reserve:messages.success-action'));
      fetchCredit();
      fetchCurrentSelfPrograms();
    }
    catch (e) {
      console.error(e);
      if (freeRestaurant) {
        setSelectedCount(getCombinedDisplayCount(reserve, cartPrograms));
      }
      else {
        countRef.current = countRef.current + 1;
        reserve.selectedCount = countRef.current;
        setSelectedCount(countRef.current);
      }
    }
    finally {
      setIsDecrementing(false);
      setLocked(false);
    }
  }, [
    cartPrograms,
    reserve,
    setSelectedCount,
    freeRestaurant,
    fetchCredit,
    messageAPI,
    removeFromCart,
    t,
    fetchCurrentSelfPrograms,
    apiVersion,
    setLocked,
    meal,
  ]);

  const handleSellReserve = useCallback(async (reserveId: number) => {
    try {
      await ReserveHttp.sellReserve(reserveId);
      setSaleable(false);
      setForSale(true);
      messageAPI.success(t('reserve:messages.success-action'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
    }
  }, [setSaleable, setForSale, fetchCredit, messageAPI, t]);

  const handleCancelSoldReserve = useCallback(async (reserveId: number) => {
    try {
      await ReserveHttp.cancelSoldReserve(reserveId);
      setSaleable(true);
      setForSale(false);
      messageAPI.success(t('reserve:messages.success-action'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
    }
  }, [setSaleable, fetchCredit, messageAPI, t]);

  const handleBuyReserve = useCallback(async (reserveId: number) => {
    try {
      await ReserveHttp.buyFreeFood(reserveId);
      setSelectedCount(c => c + 1);
      setBuyableFreeFood(false);
      messageAPI.success(t('reserve:messages.success-non-subsidized-purchase'));
      fetchCredit();
    }
    catch (e) {
      console.error(e);
    }
  }, [setBuyableFreeFood, fetchCredit, messageAPI, setSelectedCount, t]);

  if (showBulkFlow) {
    return (
      <BulkReserveControls
        meal={meal}
        reserve={reserve}
        selectedCount={selectedCount}
        setSelectedCount={setSelectedCount}
        messageAPI={messageAPI}
        locked={locked}
        setLocked={setLocked}
        isNewReserveMode={isNewReserveMode}
        reservable={reservable}
      />
    );
  }

  return (
    reservable && selectedCount === 0 ? (
      <AppButton
        size="small"
        appVariant="glass"
        className="button-reserve"
        onClick={() => handleReserve()}
        loading={isIncrementing || isDecrementing}
        disabled={locked || isIncDisabled}
      >
        {t('reserve:labels.reserve')}
      </AppButton>
    ) :
      <div className="reserve-buttons">
        {reserve.changableSelf && (<ActionPlaceholder />)}
        <ActionPlaceholder>
          {forSale && reserve.sold === false && (
            <Popconfirm
              okText={t('common:labels.yes')}
              cancelText={t('common:labels.no')}
              onConfirm={() => handleCancelSoldReserve(reserve.id)}
              title={t('reserve:messages.confirm-sell')}
            >
              <IcPlusBasket
                fill="red"
                role="button"
                width={28}
                height={28}
                className="reserve-action"
              />
            </Popconfirm>
          )}
          {buyableFreeFood && (
            <Popconfirm
              okText={t('common:labels.yes')}
              onConfirm={() => handleBuyReserve(meal.programId)}
              title={(<>
                {t('reserve:messages.buy-non-subsidized')} <br />
                {t('reserve:messages.confirm-buy-non-subsidized-fee', { fee: meal.buyableFreeFoodToll })}
              </>)}
            >
              <IcPlusBasket
                fill="#3498db"
                role="button"
                width={28}
                height={28}
                className="reserve-action"
              />
            </Popconfirm>
          )}
          {saleable && (
            <Popconfirm
              okText={t('common:labels.yes')}
              onConfirm={() => handleSellReserve(reserve.id)}
              title={(<>
                {t('reserve:messages.put-sell-non-subsidized')} <br />
                {t('reserve:messages.confirm-put-sell-non-subsidized-fee', { fee: reserve.saleableFreeFoodToll })}
              </>)}
            >
              <IcPlusBasket
                fill="green"
                role="button"
                width={28}
                height={28}
                className="reserve-action"
              />
            </Popconfirm>
          )}
        </ActionPlaceholder>
        {reservable && (
          <AppButton
            appVariant="glass"
            onClick={() => handleReserve(1)}
            disabled={isIncDisabled || isIncrementing || isDecrementing || locked}
            className="reserve-act-btn reserve-inc">
            {isIncrementing ?
              <LoadingOutlined style={{ fontSize: '16px' }} spin /> :
              <LuPlus className="text-2xl" />
            }
          </AppButton>
        )}
        <div className="reserve-selected-count">
          {!reservable && selectedCount === 0 ? '' :
            `${selectedCount} ${t('common:labels.no-')}`
          }
        </div>
        {cancelable && (
          <AppButton
            appVariant="glass"
            className="reserve-act-btn reserve-dec"
            disabled={isDecDisabled || isDecrementing || isIncrementing || locked}
            onClick={handleCancelReserve}
          >
            {isDecrementing ?
              <LoadingOutlined style={{ fontSize: '16px' }} spin /> :
              <LuMinus className="text-2xl" />
            }
          </AppButton>
        )}
        <ActionPlaceholder>
          {reserve.changableSelf && (
            <SlRefresh
              role="button"
              width={35}
              height={35}
              className="reserve-action"
              onClick={() => onChangeSelf()}
              style={{ fontSize: '1.25rem' }}
            />
          )}
        </ActionPlaceholder>
        {!cancelable && reserve.changableSelf && (<ActionPlaceholder />)}
      </div>
  );
}

function ActionPlaceholder({ children = <></> }: { children?: React.ReactElement | React.ReactElement[] }) {
  return (
    <Flex inlineFlex align="center" justify="center" className="w-[25px] h-[25px]">
      {children}
    </Flex>
  );
}
